/**
 * Shared FireCMS entity actions to translate site_chrome / page_copy via Content Engine.
 */
import React from "react";
import type { EntityAction } from "@firecms/core";
import { TranslateIcon } from "@firecms/ui";

type TranslateMode = "force" | "fill_empty" | "stale_only";

function createTranslateAction(opts: {
  name: string;
  endpoint: "/translate-site-chrome" | "/translate-page-copy";
  mode: TranslateMode;
}): EntityAction {
  return {
    name: opts.name,
    icon: <TranslateIcon />,
    onClick: async ({ entity, context }) => {
      const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
      if (!contentEngineUrl || !entity?.id) return;
      const snackbar = context.snackbarController;
      try {
        const idToken = await context.authController.getAuthToken();
        if (!idToken?.trim()) {
          snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
          return;
        }
        const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}${opts.endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ document_id: entity.id, mode: opts.mode }),
        });
        const data = (await res.json()) as {
          ok?: boolean;
          message?: string;
          error?: string;
          skipped?: boolean;
        };
        if (res.ok && data.ok) {
          snackbar.open({
            type: data.skipped ? "info" : "success",
            message: data.message ?? "Translation complete. Reload to see updates.",
          });
        } else {
          snackbar.open({ type: "error", message: data.error ?? "Translation failed" });
        }
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Translation request failed",
        });
      }
    },
  };
}

export function createSiteChromeTranslateActions(): EntityAction[] {
  return [
    createTranslateAction({
      name: "Translate all",
      endpoint: "/translate-site-chrome",
      mode: "force",
    }),
    createTranslateAction({
      name: "Fill untranslated",
      endpoint: "/translate-site-chrome",
      mode: "fill_empty",
    }),
    createTranslateAction({
      name: "Re-translate stale",
      endpoint: "/translate-site-chrome",
      mode: "stale_only",
    }),
  ];
}

export function createPageCopyTranslateActions(): EntityAction[] {
  return [
    createTranslateAction({
      name: "Translate all",
      endpoint: "/translate-page-copy",
      mode: "force",
    }),
    createTranslateAction({
      name: "Fill untranslated",
      endpoint: "/translate-page-copy",
      mode: "fill_empty",
    }),
    createTranslateAction({
      name: "Re-translate stale",
      endpoint: "/translate-page-copy",
      mode: "stale_only",
    }),
  ];
}
